import { useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { FileSpreadsheet } from "lucide-react";
import { api, apiErr, money } from "./lib/api";
import { Btn } from "./ui";

export function ExcelButton({ path, params, testid = "export-excel-btn" }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const res = await api.get(path, { params, responseType: "blob" });
      const m = /filename="?([^";]+)"?/.exec(res.headers["content-disposition"] || "");
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = m ? m[1] : "export.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return <Btn data-testid={testid} variant="ghost" disabled={busy} onClick={run}><FileSpreadsheet size={16} /> {busy ? "Preparing…" : "Export to Excel"}</Btn>;
}

function Cell({ label, value, tone = "text-slate-100" }) {
  return <div className="min-w-[120px]"><div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{label}</div><div className={`font-mono font-semibold ${tone}`}>{value}</div></div>;
}

export function InvoiceTotalsBar({ totals, skip, limit, total }) {
  if (!totals) return null;
  const page = Math.floor(skip / limit) + 1;
  const upto = Math.min(skip + limit, total);
  const row = (label, t, testid) => (
    <div className="flex flex-wrap items-center gap-6 px-5 py-3" data-testid={testid}>
      <div className="w-56 text-xs text-slate-400">{label}<div className="text-slate-500">{t.count} invoice{t.count !== 1 ? "s" : ""} · excl. cancelled</div></div>
      <Cell label="Total" value={money(t.total)} />
      <Cell label="Paid" value={money(t.paid)} tone="text-emerald-400" />
      <Cell label="Balance" value={money(t.balance)} tone="text-amber-400" />
    </div>
  );
  return (
    <div className="border-t border-slate-800 bg-slate-900/40 divide-y divide-slate-800/60" data-testid="invoice-totals-bar">
      {row(`Through page ${page} (rows 1–${upto})`, totals.cumulative, "totals-cumulative")}
      {total > upto && row(`All ${total} matching invoices`, totals.all, "totals-all")}
    </div>
  );
}

export function ExportTotalsBar({ totals, skip, limit, total }) {
  if (!totals) return null;
  const page = Math.floor(skip / limit) + 1;
  const upto = Math.min(skip + limit, total);
  const row = (label, t, testid) => (
    <div className="flex flex-wrap items-center gap-6 px-5 py-3" data-testid={testid}>
      <div className="w-56 text-xs text-slate-400">{label}<div className="text-slate-500">{t.count} invoice{t.count !== 1 ? "s" : ""} · {t.paid_count} paid · excl. cancelled</div></div>
      {t.by_currency.map((c) => <Cell key={c.currency} label={`${c.currency} (${c.count})`} value={money(c.total, c.currency)} />)}
      <Cell label="INR equivalent" value={money(t.total_inr, "INR")} tone="text-emerald-400" />
    </div>
  );
  return (
    <div className="border-t border-slate-800 bg-slate-900/40 divide-y divide-slate-800/60" data-testid="export-totals-bar">
      {row(`Through page ${page} (rows 1–${upto})`, totals.cumulative, "ex-totals-cumulative")}
      {total > upto && row(`All ${total} matching invoices`, totals.all, "ex-totals-all")}
    </div>
  );
}

export function SortToggle({ sort, setSort, testid = "sort-toggle" }) {
  return (
    <div className="flex items-center gap-1.5" data-testid={testid}>
      {[["date", "Invoice date"], ["created", "Recently created"]].map(([v, l]) => (
        <button key={v} data-testid={`${testid}-${v}`} onClick={() => setSort(v)}
          className={`px-3 py-2 rounded-lg text-xs font-medium border transition-colors ${sort === v ? "bg-sky-500/20 text-sky-300 border-sky-500/40" : "bg-slate-800/40 text-slate-400 border-slate-700 hover:text-slate-200"}`}>{l}</button>
      ))}
    </div>
  );
}

export function isNew(iso, hours = 48) {
  if (!iso) return false;
  const t = new Date(iso.replace(" ", "T")).getTime();
  return Number.isFinite(t) && Date.now() - t < hours * 3600 * 1000;
}

export function NewBadge({ createdAt }) {
  if (!isNew(createdAt)) return null;
  return <span className="ml-1.5 inline-block px-1.5 py-0.5 rounded text-[10px] font-mono font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 align-middle" data-testid="new-badge">NEW</span>;
}

export function fyOf(dateStr) {
  if (!dateStr) return "";
  const y = Number(dateStr.slice(0, 4)), m = Number(dateStr.slice(5, 7));
  const s = m >= 4 ? y : y - 1;
  return `${s}-${String(s + 1).slice(2)}`;
}

export function DateFyWarning({ date, invNumber }) {
  const today = new Date().toISOString().slice(0, 10);
  const fy = fyOf(date), cur = fyOf(today);
  if (!date || fy === cur) return null;
  return (
    <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-200" data-testid="date-fy-warning">
      <span className="font-bold">⚠</span>
      <span>Invoice date <b>{date.split("-").reverse().join("-")}</b> falls in <b>FY {fy}</b> (current is FY {cur}). It will be numbered in that series{invNumber ? <> as <b className="font-mono">{invNumber}</b></> : ""} and appear under {fy} in the list — use <b>“Recently created”</b> sort to find it quickly.</span>
    </div>
  );
}

// URL-synced state so filters survive Back navigation / refresh
export function useUrlState(key, def = "") {
  const [sp, setSp] = useSearchParams();
  const value = sp.has(key) ? sp.get(key) : def;
  const set = useCallback((v) => {
    // read live URL (not stale closure) so several setters in one event don't clobber each other
    const n = new URLSearchParams(window.location.search);
    if (v === "" || v == null || String(v) === String(def)) n.delete(key); else n.set(key, String(v));
    setSp(n, { replace: true });
  }, [key, def, setSp]);
  return [value, set];
}
export function useUrlNumber(key, def = 0) {
  const [v, set] = useUrlState(key, String(def));
  return [Number(v) || def, (n) => set(String(n))];
}
